import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, User, LogOut, ChevronDown, Package, MapPin } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';

export default function CustomerNav() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  return (
    <header data-testid="customer-nav" className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Zanova"
              className="h-8 sm:h-10 w-auto object-contain"
            />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" data-testid="nav-home" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Home</Link>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="nav-categories-trigger" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                Categories <ChevronDown className="h-3.5 w-3.5" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuItem asChild><Link to="/category/fruits">Fruits</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/category/vegetables">Vegetables</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/category/dairy">Dairy</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/category/grains-pulses">Grains & Pulses</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/category/spices">Spices</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/category/honey-preserves">Honey & Preserves</Link></DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/products" data-testid="nav-products" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">All Products</Link>
            <Link to="/our-story" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Our Story</Link>
            <Link to="/contact" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">Contact</Link>
          </nav>
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="hidden sm:flex items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input data-testid="nav-search-input" type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..." className="h-9 w-48 lg:w-64 pl-9 pr-3 rounded-full bg-muted/60 border-0 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 placeholder:text-muted-foreground/60" />
              </div>
            </form>
            <Link to="/cart" data-testid="nav-cart-button" className="relative p-2 rounded-full hover:bg-muted transition-colors">
              <ShoppingCart className="h-5 w-5 text-foreground" />
              {cartCount > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-semibold">{cartCount}</span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="nav-user-menu" className="flex items-center gap-2 p-1.5 rounded-full hover:bg-muted transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-foreground">{user.name?.[0]?.toUpperCase() || 'U'}</span>
                    </div>
                  )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user.name}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/profile" data-testid="nav-profile-link" className="flex items-center gap-2"><User className="h-4 w-4" /> My Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/profile?tab=orders" className="flex items-center gap-2"><Package className="h-4 w-4" /> My Orders</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/profile?tab=addresses" className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Addresses</Link></DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem asChild><Link to="/admin" className="flex items-center gap-2"><User className="h-4 w-4" /> Admin Panel</Link></DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="nav-logout-button" onClick={logout} className="flex items-center gap-2 text-destructive"><LogOut className="h-4 w-4" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth"><Button data-testid="nav-login-button" variant="default" size="sm" className="rounded-full px-5">Sign In</Button></Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
